import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { marketingWebAPI } from '../../api/api';
import {
  Users,
  BarChart3,
  Search,
  TrendingUp,
  TrendingDown,
  Mail,
  Activity,
  Sparkles,
  ArrowRight,
  Calendar,
  Minus,
  Euro,
  UserPlus,
  FileText,
  CheckCircle2,
  Percent,
  Receipt,
  Globe,
  MousePointerClick,
  Target,
  Trophy,
  Info,
  Link2,
  RefreshCw,
} from 'lucide-react';

/**
 * /marketing/web/ — Dashboard principal del canal Web.
 *
 * Bloques:
 *  1) KPIs de leads (últimos 30 días)
 *  2) Tarjetas a las subpáginas (analytics, seo, email, estado, ads)
 *  3) Comparativa antes/después del switchover web (Astro, 15/05/2026)
 *     cruza datos del negocio (facturación, clientes, ratio, ticket medio)
 *     con datos de la web (sesiones, conversiones, impresiones, posición).
 *  4) Comportamiento en la web (GA4): páginas, tiempo, scroll, botones,
 *     embudo hasta Contacto, dispositivos, horas y páginas de entrada.
 */
export default function MarketingWeb() {
  const [kpis, setKpis] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    marketingWebAPI.kpis(30)
      .then((res) => setKpis(res.data))
      .catch((e) => console.error('Error cargando KPIs:', e))
      .finally(() => setLoading(false));
  }, []);

  const subpages = [
    { slug: 'leads', label: 'Leads', icon: Users, descripcion: 'Formulario contacto + descarga catálogo', activo: true },
    { slug: 'analytics', label: 'Analytics (GA4)', icon: BarChart3, descripcion: 'Visitas, páginas top, conversiones', activo: true },
    { slug: 'seo', label: 'SEO (Search Console)', icon: Search, descripcion: 'Queries, impressions, CTR, posición', activo: true },
    { slug: 'ads', label: 'Google Ads', icon: TrendingUp, descripcion: 'Spend, CPL, conversiones', activo: true },
    { slug: 'email', label: 'Mailchimp', icon: Mail, descripcion: 'Suscriptores, campañas, open rate', activo: true },
    { slug: 'estado', label: 'Estado de la web', icon: Activity, descripcion: 'Uptime, Core Web Vitals, último deploy', activo: true },
    { slug: 'ai-visibility', label: 'AI Visibility', icon: Sparkles, descripcion: 'Si Gemini (con búsqueda) cita a Duetto al preguntar por catering', activo: true },
    { slug: 'backlinks', label: 'Backlinks', icon: Link2, descripcion: 'Enlaces SEO comprados: compra, verificación y cruce GSC', activo: true },
  ];

  const leads = kpis?.leads;

  return (
    <div className="p-6 max-w-[1600px] mx-auto">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-duetto-primary">Marketing · Web</h1>
          <p className="text-sm text-gray-500 mt-1">
            Dashboard de <span className="font-mono">duettocatering.com</span> · últimos 30 días
          </p>
        </div>
      </div>

      {/* KPIs de leads */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-8">
        <KpiCard
          label="Leads totales"
          value={loading ? '—' : leads?.total ?? 0}
          icon={Users}
          color="bg-duetto-primary text-white"
        />
        <KpiCard
          label="Desde formulario"
          value={loading ? '—' : leads?.contacto ?? 0}
        />
        <KpiCard
          label="Desde catálogo"
          value={loading ? '—' : leads?.catalogo ?? 0}
        />
        <KpiCard
          label="Sin contactar"
          value={loading ? '—' : leads?.nuevos_sin_contactar ?? 0}
          warning={(leads?.nuevos_sin_contactar ?? 0) > 0}
        />
        <KpiCard
          label="Tasa conversión"
          value={loading ? '—' : `${leads?.tasa_conversion ?? 0}%`}
          icon={TrendingUp}
        />
      </div>

      {/* Subpáginas */}
      <h2 className="text-lg font-semibold text-gray-700 mb-3">Secciones</h2>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {subpages.map((p) => (
          <Link
            key={p.slug}
            to={p.activo ? `/marketing/web/${p.slug}/` : '#'}
            className={`bg-white border rounded-lg p-5 flex items-start gap-3 transition-shadow ${
              p.activo ? 'hover:shadow-lg' : 'opacity-50 pointer-events-none'
            }`}
          >
            <p.icon className="w-6 h-6 text-duetto-primary shrink-0 mt-1" />
            <div className="flex-1 min-w-0">
              <div className="font-semibold text-sm flex items-center gap-2">
                {p.label}
                {p.activo && <ArrowRight className="w-4 h-4 text-gray-400" />}
              </div>
              <p className="text-xs text-gray-500 mt-1">{p.descripcion}</p>
              {!p.activo && (
                <span className="inline-block mt-2 text-[10px] uppercase tracking-wider text-gray-400">
                  Próximamente
                </span>
              )}
            </div>
          </Link>
        ))}
      </div>

      {/* Comparativa antes/después del switchover */}
      <ComparativaSwitchover />

      {/* Comportamiento en la web (debajo de Presencia online) */}
      <ComportamientoWeb />
    </div>
  );
}

function KpiCard({ label, value, icon: Icon, color = 'bg-white', warning = false }) {
  const cls = warning
    ? 'border-yellow-300 bg-yellow-50'
    : color === 'bg-white' ? 'border bg-white' : color;
  return (
    <div className={`rounded-lg p-4 ${cls}`}>
      <div className="flex items-center justify-between mb-1">
        <span className="text-xs uppercase tracking-wide font-semibold opacity-70">{label}</span>
        {Icon && <Icon className="w-4 h-4 opacity-50" />}
      </div>
      <div className="text-3xl font-bold">{value}</div>
    </div>
  );
}

// ───────────────────────────────────────────────────────────────────────
// Comparativa antes/después del switchover web (Astro)
// ───────────────────────────────────────────────────────────────────────

const SWITCHOVER_DEFAULT = '2026-05-15';

const fmtEur = (n) => {
  if (n === null || n === undefined) return '—';
  return new Intl.NumberFormat('es-ES', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(n);
};
const fmtInt = (n) => {
  if (n === null || n === undefined) return '—';
  return new Intl.NumberFormat('es-ES').format(n);
};
const fmtPct = (n) => (n === null || n === undefined ? '—' : `${n}%`);
const fmtPos = (n) => (!n ? '—' : n.toFixed(1));
const fmtDia = (s) => {
  if (!s) return '—';
  const [y, m, d] = s.split('-');
  return `${d}/${m}/${y.slice(2)}`;
};

function ComparativaSwitchover() {
  const [corte, setCorte] = useState(SWITCHOVER_DEFAULT);
  const [dias, setDias] = useState(30);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    setLoading(true);
    setError(null);
    marketingWebAPI.comparativa({ corte, dias })
      .then((res) => setData(res.data))
      .catch((e) => {
        console.error('Error cargando comparativa:', e);
        setError(e?.response?.data?.detail || 'No se pudieron cargar los datos');
      })
      .finally(() => setLoading(false));
  }, [corte, dias]);

  const metricas = useMemo(() => buildMetricas(data), [data]);

  return (
    <section className="mt-12">
      {/* Cabecera */}
      <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-4 mb-5">
        <div>
          <h2 className="text-xl font-bold text-gray-900">
            Antes vs. después del cambio de web
          </h2>
          <p className="text-sm text-gray-500 mt-1">
            Compara la actividad comercial y la presencia online en los días previos
            y posteriores al hito.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2 text-sm">
          <div className="flex items-center gap-2 px-3 py-2 bg-white border rounded-lg shadow-sm">
            <Calendar className="w-4 h-4 text-duetto-primary" />
            <span className="text-xs text-gray-500">Hito</span>
            <input
              type="date"
              value={corte}
              onChange={(e) => setCorte(e.target.value)}
              className="outline-none text-sm font-medium"
            />
          </div>
          <div className="flex items-center gap-2 px-3 py-2 bg-white border rounded-lg shadow-sm">
            <span className="text-xs text-gray-500">Ventana</span>
            <select
              value={dias}
              onChange={(e) => setDias(Number(e.target.value))}
              className="outline-none text-sm font-medium bg-transparent"
            >
              <option value={7}>± 7 días</option>
              <option value={14}>± 14 días</option>
              <option value={30}>± 30 días</option>
              <option value={60}>± 60 días</option>
              <option value={90}>± 90 días</option>
            </select>
          </div>
        </div>
      </div>

      {/* Pills de ventanas */}
      {data && (
        <div className="flex flex-wrap items-center gap-2 mb-6 text-xs">
          <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-gray-100 text-gray-700 rounded-full">
            <span className="w-2 h-2 bg-gray-400 rounded-full" />
            <span className="font-semibold">Antes</span>
            <span className="text-gray-500">{fmtDia(data.antes.desde)} → {fmtDia(data.antes.hasta)}</span>
            <span className="text-gray-400">· {data.antes.dias_disponibles}d</span>
          </span>
          <ArrowRight className="w-4 h-4 text-gray-400" />
          <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-emerald-50 text-emerald-800 rounded-full">
            <span className="w-2 h-2 bg-emerald-500 rounded-full" />
            <span className="font-semibold">Después</span>
            <span className="text-emerald-700">
              {fmtDia(data.despues.desde)} → {fmtDia(data.despues.hasta)}
            </span>
            <span className="text-emerald-600">· {data.despues.dias_disponibles}d</span>
          </span>
          {data.despues.dias_disponibles < data.dias_solicitados && (
            <span className="inline-flex items-center gap-1 px-2 py-1 bg-amber-50 text-amber-800 rounded-full text-[11px]">
              <Info className="w-3 h-3" />
              Ventana parcial: aún quedan {data.dias_solicitados - data.despues.dias_disponibles} días por acumular
            </span>
          )}
        </div>
      )}

      {loading && (
        <div className="bg-white border rounded-lg p-8 text-center text-sm text-gray-500">
          Cargando comparativa…
        </div>
      )}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg p-4 text-sm">
          {error}
        </div>
      )}

      {!loading && !error && data && (
        <>
          <div className="mb-2 text-xs text-gray-500 flex items-start gap-2 bg-blue-50/40 border border-blue-100 rounded-lg p-3">
            <Info className="w-4 h-4 text-blue-500 shrink-0 mt-0.5" />
            <span>
              Solo cuenta lo que vino del formulario o del catálogo de la web. Para que
              "leads convertidos" reflejen la realidad, marca cada lead como{' '}
              <span className="font-mono">convertido</span> y vincula su cliente/presupuesto
              desde <Link to="/marketing/web/leads/" className="text-duetto-primary font-semibold underline">/marketing/web/leads/</Link>.
            </span>
          </div>
          <SeccionMetricas
            titulo="Negocio atribuido a la web"
            subtitulo="Leads, presupuestos y valor cerrado a partir de los formularios de duettocatering.com"
            metricas={metricas.negocio}
          />
          <SeccionMetricas
            titulo="Presencia online"
            subtitulo="Tráfico, conversiones y posicionamiento orgánico"
            metricas={metricas.web}
            className="mt-8"
          />
        </>
      )}
    </section>
  );
}

function SeccionMetricas({ titulo, subtitulo, metricas, className = '' }) {
  return (
    <div className={className}>
      <div className="mb-3">
        <h3 className="text-sm font-bold uppercase tracking-wider text-gray-500">{titulo}</h3>
        <p className="text-xs text-gray-400 mt-0.5">{subtitulo}</p>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {metricas.map((m) => (
          <MetricaCard key={m.key} {...m} />
        ))}
      </div>
    </div>
  );
}

function MetricaCard({ label, hint, icon: Icon, antes, despues, delta, deltaUnit, betterDown }) {
  const tone = deltaTone(delta, betterDown);
  const accent = {
    good: 'border-emerald-200 bg-gradient-to-br from-emerald-50/40 to-white',
    bad: 'border-red-200 bg-gradient-to-br from-red-50/40 to-white',
    neutral: 'border-gray-200 bg-white',
  }[tone];

  return (
    <div className={`relative border rounded-xl p-4 ${accent} hover:shadow-md transition-shadow`}>
      <div className="flex items-start justify-between gap-2 mb-3">
        <div className="flex items-center gap-2 min-w-0">
          {Icon && (
            <span className="w-8 h-8 rounded-lg bg-white shadow-sm border flex items-center justify-center shrink-0">
              <Icon className="w-4 h-4 text-duetto-primary" />
            </span>
          )}
          <div className="min-w-0">
            <div className="text-sm font-semibold text-gray-800 truncate">{label}</div>
            {hint && <div className="text-[11px] text-gray-400 truncate">{hint}</div>}
          </div>
        </div>
        <DeltaPill value={delta} unit={deltaUnit} betterDown={betterDown} />
      </div>

      <div className="flex items-baseline justify-between gap-4">
        <div>
          <div className="text-[10px] uppercase tracking-wider text-gray-400 font-semibold">Antes</div>
          <div className="text-base text-gray-500 font-mono mt-0.5">{antes}</div>
        </div>
        <ArrowRight className="w-4 h-4 text-gray-300 shrink-0 mt-3" />
        <div className="text-right">
          <div className="text-[10px] uppercase tracking-wider text-gray-400 font-semibold">Después</div>
          <div className="text-2xl text-gray-900 font-bold font-mono mt-0.5 tabular-nums">{despues}</div>
        </div>
      </div>
    </div>
  );
}

function deltaTone(value, betterDown) {
  if (value === null || value === undefined || value === 0) return 'neutral';
  const isPositive = value > 0;
  const esMejor = betterDown ? !isPositive : isPositive;
  return esMejor ? 'good' : 'bad';
}

function DeltaPill({ value, unit = '%', betterDown = false }) {
  if (value === null || value === undefined) {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-gray-100 text-gray-400 text-[11px] font-semibold">
        <Minus className="w-3 h-3" />
        sin baseline
      </span>
    );
  }
  const tone = deltaTone(value, betterDown);
  const cls = {
    good: 'bg-emerald-100 text-emerald-800',
    bad: 'bg-red-100 text-red-700',
    neutral: 'bg-gray-100 text-gray-500',
  }[tone];
  const isPositive = value > 0;
  const isZero = value === 0;
  const Icon = isZero ? Minus : isPositive ? TrendingUp : TrendingDown;
  const signo = isPositive ? '+' : '';
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-[11px] font-bold ${cls}`}>
      <Icon className="w-3 h-3" />
      {signo}{value}{unit}
    </span>
  );
}

function buildMetricas(data) {
  if (!data) return { negocio: [], web: [] };
  const a = data.antes.negocio;
  const d = data.despues.negocio;
  const aw = data.antes.web;
  const dw = data.despues.web;
  const delta = data.delta;

  const negocio = [
    {
      key: 'leads',
      label: 'Leads desde la web',
      hint: 'Formulario de contacto + descarga de catálogo',
      icon: UserPlus,
      antes: fmtInt(a.leads.total),
      despues: fmtInt(d.leads.total),
      delta: delta.leads_pct,
    },
    {
      key: 'leads_conv',
      label: 'Leads convertidos',
      hint: 'Leads marcados como "convertido" desde /leads',
      icon: CheckCircle2,
      antes: fmtInt(a.leads.convertidos),
      despues: fmtInt(d.leads.convertidos),
      delta: delta.leads_convertidos_pct,
    },
    {
      key: 'ratio_leads',
      label: 'Ratio leads → cliente',
      hint: 'Convertidos / leads totales',
      icon: Percent,
      antes: fmtPct(a.leads.ratio_pct),
      despues: fmtPct(d.leads.ratio_pct),
      delta: delta.ratio_leads_pp,
      deltaUnit: ' pp',
    },
    {
      key: 'presup_generados',
      label: 'Presupuestos generados',
      hint: 'Presupuestos creados desde leads de la web',
      icon: FileText,
      antes: fmtInt(a.presupuestos.total.enviados),
      despues: fmtInt(d.presupuestos.total.enviados),
      delta: delta.presupuestos_enviados_pct,
    },
    {
      key: 'presup_cerrados',
      label: 'Presupuestos cerrados',
      hint: 'De los generados, los que el cliente confirmó',
      icon: CheckCircle2,
      antes: fmtInt(a.presupuestos.total.confirmados),
      despues: fmtInt(d.presupuestos.total.confirmados),
      delta: delta.presupuestos_confirmados_pct,
    },
    {
      key: 'ratio_cierre',
      label: 'Ratio de cierre',
      hint: 'Confirmados / generados',
      icon: Percent,
      antes: fmtPct(a.presupuestos.total.ratio_pct),
      despues: fmtPct(d.presupuestos.total.ratio_pct),
      delta: delta.ratio_conversion_pp,
      deltaUnit: ' pp',
    },
    {
      key: 'valor_cerrado',
      label: 'Valor cerrado',
      hint: 'Importe total de presupuestos confirmados de la web',
      icon: Euro,
      antes: fmtEur(a.valor_cerrado.total),
      despues: fmtEur(d.valor_cerrado.total),
      delta: delta.valor_cerrado_pct,
    },
    {
      key: 'ticket',
      label: 'Ticket medio',
      hint: 'Valor cerrado / presupuestos confirmados',
      icon: Receipt,
      antes: fmtEur(a.ticket_medio.total),
      despues: fmtEur(d.ticket_medio.total),
      delta: delta.ticket_medio_pct,
    },
  ];

  const web = [
    {
      key: 'sessions',
      label: 'Sesiones',
      hint: 'GA4 · visitas iniciadas a la web',
      icon: Globe,
      antes: fmtInt(aw.analytics.sessions),
      despues: fmtInt(dw.analytics.sessions),
      delta: delta.sessions_pct,
    },
    {
      key: 'users',
      label: 'Usuarios únicos',
      hint: 'GA4 · personas distintas',
      icon: Users,
      antes: fmtInt(aw.analytics.users),
      despues: fmtInt(dw.analytics.users),
      delta: delta.users_pct,
    },
    {
      key: 'conversions',
      label: 'Conversiones web',
      hint: 'GA4 · eventos clave (formulario, descarga, etc.)',
      icon: Target,
      antes: fmtInt(aw.analytics.conversions),
      despues: fmtInt(dw.analytics.conversions),
      delta: delta.conversions_pct,
    },
    {
      key: 'impressions',
      label: 'Impresiones en Google',
      hint: 'Search Console · veces que aparecimos en SERP',
      icon: Search,
      antes: fmtInt(aw.seo.impressions),
      despues: fmtInt(dw.seo.impressions),
      delta: delta.impressions_pct,
    },
    {
      key: 'clicks',
      label: 'Clicks orgánicos',
      hint: 'Search Console · clicks desde resultados de Google',
      icon: MousePointerClick,
      antes: fmtInt(aw.seo.clicks),
      despues: fmtInt(dw.seo.clicks),
      delta: delta.clicks_pct,
    },
    {
      key: 'posicion',
      label: 'Posición media',
      hint: 'Search Console · 1.0 = primero (cuanto más bajo, mejor)',
      icon: Trophy,
      antes: fmtPos(aw.seo.position_media),
      despues: fmtPos(dw.seo.position_media),
      delta: delta.position_media_pp,
      deltaUnit: ' pos',
      betterDown: true,
    },
  ];

  return { negocio, web };
}

// ───────────────────────────────────────────────────────────────────────
// Comportamiento en la web (GA4): páginas, tiempo, scroll, botones,
// embudo hasta Contacto, dispositivos, horas y páginas de entrada.
// Lee la caché diaria del ERP (sync 03:00 o "Actualizar ahora").
// ───────────────────────────────────────────────────────────────────────

const RANGOS_COMPORTAMIENTO = [7, 30, 90];

const NOMBRES_EVENTO = {
  cta_click: 'Botones de llamada a la acción',
  telefono_click: 'Clic en el teléfono',
  whatsapp_click: 'Clic en WhatsApp',
  catalogo_descarga: 'Descarga del catálogo',
  email_click: 'Clic en el email',
  salida_externa: 'Enlaces a otras webs',
  formulario_enviado: 'Formularios enviados',
  generate_lead: 'Leads (evento GA4)',
  form_submit: 'Envíos de formulario (GA4)',
};

const NOMBRES_DISPOSITIVO = { mobile: 'Móvil', desktop: 'Escritorio', tablet: 'Tablet' };

const TEXTO_SIN_EVENTOS = 'Los eventos de clic empiezan a llegar 24 a 48 h después del despliegue de la web.';

const segundosLegibles = (s) => {
  const n = Math.round(Number(s) || 0);
  if (n < 60) return `${n} s`;
  const min = Math.floor(n / 60);
  const resto = n % 60;
  return resto ? `${min} min ${resto} s` : `${min} min`;
};

const tasaPct = (x) => (x === null || x === undefined ? '-' : `${Math.round(Number(x) * 100)}%`);

const fechaHoraCorta = (iso) => {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleString('es-ES', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit', timeZone: 'Europe/Madrid' });
};

function ComportamientoWeb() {
  const [dias, setDias] = useState(30);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sincronizando, setSincronizando] = useState(false);
  const [mensaje, setMensaje] = useState(null);

  const cargar = useCallback(() => {
    setLoading(true);
    setError(null);
    return marketingWebAPI.comportamiento(dias)
      .then((res) => setData(res.data))
      .catch((e) => {
        console.error('Error cargando comportamiento web:', e);
        const detalle = e?.response?.data?.detail;
        setError(typeof detalle === 'string' ? detalle : 'No se pudo cargar el comportamiento de la web');
      })
      .finally(() => setLoading(false));
  }, [dias]);

  useEffect(() => {
    cargar();
  }, [cargar]);

  const actualizar = async () => {
    setSincronizando(true);
    setMensaje(null);
    try {
      const res = await marketingWebAPI.comportamientoSync(7);
      setMensaje({ tipo: 'ok', texto: `Datos actualizados desde GA4 (${res.data?.dias_sincronizados ?? 0} días)` });
      await cargar();
    } catch (e) {
      const detalle = e?.response?.data?.detail;
      setMensaje({ tipo: 'error', texto: typeof detalle === 'string' ? detalle : 'No se pudo actualizar desde GA4' });
    } finally {
      setSincronizando(false);
    }
  };

  const sinDatos = data && !data.dias_con_datos;
  const ultimo = fechaHoraCorta(data?.ultimo_sync);

  return (
    <section className="mt-12" data-testid="comportamiento-web">
      <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-4 mb-4">
        <div className="min-w-0">
          <h2 className="text-xl font-bold text-gray-900">Comportamiento en la web</h2>
          <p className="text-sm text-gray-500 mt-1">
            Qué hacen las visitas dentro de duettocatering.com: páginas, tiempo, botones y camino hasta el formulario.
          </p>
          {ultimo && <p className="text-xs text-gray-400 mt-1">Último dato guardado: {ultimo}</p>}
        </div>
        <div className="flex flex-wrap items-center gap-2 text-sm">
          <div className="inline-flex rounded-lg border bg-white shadow-sm overflow-hidden" role="group" aria-label="Periodo">
            {RANGOS_COMPORTAMIENTO.map((r) => (
              <button
                key={r}
                type="button"
                onClick={() => setDias(r)}
                aria-pressed={dias === r}
                className={`px-3 py-2 text-sm ${dias === r ? 'bg-duetto-primary text-white' : 'text-gray-700 hover:bg-gray-50'}`}
              >
                {r} días
              </button>
            ))}
          </div>
          <button
            type="button"
            onClick={actualizar}
            disabled={sincronizando}
            className="inline-flex items-center gap-2 px-3 py-2 bg-white border rounded-lg shadow-sm text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-60"
          >
            <RefreshCw className={`w-4 h-4 ${sincronizando ? 'animate-spin' : ''}`} />
            {sincronizando ? 'Actualizando…' : 'Actualizar ahora'}
          </button>
        </div>
      </div>

      {mensaje && (
        <div className={`mb-3 text-sm rounded-lg p-3 border ${mensaje.tipo === 'ok' ? 'bg-emerald-50 border-emerald-200 text-emerald-800' : 'bg-red-50 border-red-200 text-red-700'}`}>
          {mensaje.texto}
        </div>
      )}
      {data?.avisos?.length > 0 && (
        <div className="mb-4 text-xs text-amber-900 bg-amber-50 border border-amber-200 rounded-lg p-3 flex items-start gap-2">
          <Info className="w-4 h-4 shrink-0 mt-0.5" />
          <ul className="space-y-0.5">
            {data.avisos.map((a) => <li key={a}>{a}</li>)}
          </ul>
        </div>
      )}

      {loading && !data && (
        <div className="bg-white border rounded-lg p-8 text-center text-sm text-gray-500">Cargando comportamiento…</div>
      )}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg p-4 text-sm">{error}</div>
      )}

      {sinDatos && !error && (
        <div className="bg-white border rounded-lg p-6 text-sm text-gray-600">
          <p className="font-semibold text-gray-800">Todavía no hay datos de comportamiento guardados.</p>
          <p className="mt-1">{TEXTO_SIN_EVENTOS} Las páginas, el tiempo y los dispositivos aparecen en cuanto se guarde el primer día (sync de las 03:00 o Actualizar ahora).</p>
        </div>
      )}

      {data && !sinDatos && !error && (
        <div className="space-y-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <KpiCard label="Sesiones" value={fmtInt(data.totales?.sesiones)} icon={Users} />
            <KpiCard label="Tiempo medio por página" value={segundosLegibles(data.totales?.segundos_medios_por_pagina)} icon={Activity} />
            <KpiCard label="Llega a Contacto" value={fmtPct(data.totales?.pct_contacto)} icon={Percent} />
            <KpiCard label="Formularios" value={fmtInt(data.totales?.formularios)} icon={FileText} />
          </div>

          <TablaPaginas paginas={data.paginas || []} />

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <BotonesQueConvierten eventos={data.eventos || {}} detalleNoDisponible={data.eventos_detalle_no_disponible} />
            <EmbudoContacto embudo={data.embudo || {}} />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <DispositivosWeb dispositivos={data.dispositivos || []} />
            <HorasWeb horas={data.horas || []} />
          </div>

          <PaginasEntrada landing={data.landing || []} />
        </div>
      )}

      {data && (
        <div className="mt-4 text-sm">
          {data.clarity_url ? (
            <a
              href={data.clarity_url}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 text-duetto-primary font-semibold underline"
            >
              <MousePointerClick className="w-4 h-4" />
              Ver mapas de calor en Clarity
            </a>
          ) : (
            <p className="text-xs text-gray-500">
              Clarity sin configurar: falta PUBLIC_CLARITY_PROJECT_ID en Vercel y CLARITY_PROJECT_ID en el ERP.
            </p>
          )}
        </div>
      )}
    </section>
  );
}

function BloqueWeb({ titulo, subtitulo, children }) {
  return (
    <div className="bg-white border rounded-lg p-4 min-w-0">
      <h3 className="text-sm font-bold uppercase tracking-wider text-gray-500">{titulo}</h3>
      {subtitulo && <p className="text-xs text-gray-400 mt-0.5">{subtitulo}</p>}
      <div className="mt-3">{children}</div>
    </div>
  );
}

function TablaPaginas({ paginas }) {
  const [todas, setTodas] = useState(false);
  const conScroll = paginas.some((p) => p.scroll_medio !== null && p.scroll_medio !== undefined);
  const conClics = paginas.some((p) => (p.cta_clics || 0) > 0);
  const visibles = todas ? paginas : paginas.slice(0, 15);
  return (
    <BloqueWeb titulo="Páginas" subtitulo="Ordenadas por vistas. Tiempo medio = tiempo de interacción entre vistas.">
      {paginas.length === 0 ? (
        <p className="text-sm text-gray-500">Sin páginas en el periodo.</p>
      ) : (
        <>
          <div className="overflow-x-auto -mx-4 px-4">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="text-left text-xs text-gray-500 border-b">
                  <th className="py-2 pr-3 font-medium">Página</th>
                  <th className="py-2 px-2 font-medium text-right whitespace-nowrap">Vistas</th>
                  <th className="py-2 px-2 font-medium text-right whitespace-nowrap">Usuarios</th>
                  <th className="py-2 px-2 font-medium text-right whitespace-nowrap">Tiempo medio</th>
                  {conScroll && <th className="py-2 px-2 font-medium text-right whitespace-nowrap">Scroll medio</th>}
                  {conClics && <th className="py-2 pl-2 font-medium text-right whitespace-nowrap">Clics a botones</th>}
                </tr>
              </thead>
              <tbody>
                {visibles.map((p) => (
                  <tr key={p.path} className="border-b last:border-0">
                    <td className="py-2 pr-3 font-mono text-xs text-gray-800 break-all min-w-[10rem]">{p.path}</td>
                    <td className="py-2 px-2 text-right tabular-nums">{fmtInt(p.vistas)}</td>
                    <td className="py-2 px-2 text-right tabular-nums">{fmtInt(p.usuarios)}</td>
                    <td className="py-2 px-2 text-right tabular-nums whitespace-nowrap">{segundosLegibles(p.segundos_medios)}</td>
                    {conScroll && (
                      <td className="py-2 px-2 text-right tabular-nums">{p.scroll_medio === null || p.scroll_medio === undefined ? '-' : `${Math.round(p.scroll_medio)}%`}</td>
                    )}
                    {conClics && <td className="py-2 pl-2 text-right tabular-nums">{fmtInt(p.cta_clics || 0)}</td>}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {paginas.length > 15 && (
            <button type="button" onClick={() => setTodas((v) => !v)} className="mt-2 text-xs text-duetto-primary font-semibold">
              {todas ? 'Ver menos' : `Ver las ${paginas.length} páginas`}
            </button>
          )}
          {conScroll && (
            <p className="mt-2 text-[11px] text-gray-400">Scroll medio aproximado a partir de los avisos al 25, 50, 75 y 90 % de la página.</p>
          )}
        </>
      )}
    </BloqueWeb>
  );
}

function BarraHorizontal({ etiqueta, valor, maximo, detalle }) {
  const ancho = maximo ? Math.max(2, Math.round((valor / maximo) * 100)) : 0;
  return (
    <li className="text-sm">
      <div className="flex items-baseline justify-between gap-2">
        <span className="text-gray-700 min-w-0 break-words">{etiqueta}</span>
        <span className="tabular-nums text-gray-900 font-semibold shrink-0">{fmtInt(valor)}</span>
      </div>
      <div className="h-2 bg-gray-100 rounded mt-1">
        <div className="h-2 bg-duetto-primary rounded" style={{ width: `${ancho}%` }} />
      </div>
      {detalle && <p className="text-[11px] text-gray-400 mt-0.5">{detalle}</p>}
    </li>
  );
}

function BotonesQueConvierten({ eventos, detalleNoDisponible }) {
  const cta = eventos.cta || [];
  const totales = Object.entries(eventos.totales || {})
    .filter(([nombre, n]) => NOMBRES_EVENTO[nombre] && n > 0)
    .sort((a, b) => b[1] - a[1]);
  if (cta.length > 0) {
    const max = Math.max(...cta.map((c) => c.clics));
    return (
      <BloqueWeb titulo="Botones que convierten" subtitulo="Clics por botón y por zona de la página">
        <ul className="space-y-2">
          {cta.slice(0, 12).map((c) => (
            <BarraHorizontal key={`${c.cta}|${c.ubicacion}`} etiqueta={`${c.cta} · ${c.ubicacion}`} valor={c.clics} maximo={max} />
          ))}
        </ul>
      </BloqueWeb>
    );
  }
  const max = totales.length ? Math.max(...totales.map(([, n]) => n)) : 0;
  return (
    <BloqueWeb titulo="Botones que convierten" subtitulo="Total de clics por tipo">
      {detalleNoDisponible && (
        <p className="mb-2 text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded p-2">
          Sin detalle por botón: registrar las dimensiones personalizadas cta y ubicacion en GA4.
        </p>
      )}
      {totales.length === 0 ? (
        <p className="text-sm text-gray-500">{TEXTO_SIN_EVENTOS}</p>
      ) : (
        <ul className="space-y-2">
          {totales.map(([nombre, n]) => (
            <BarraHorizontal key={nombre} etiqueta={NOMBRES_EVENTO[nombre]} valor={n} maximo={max} />
          ))}
        </ul>
      )}
    </BloqueWeb>
  );
}

function EmbudoContacto({ embudo }) {
  const pasos = [
    { etiqueta: 'Sesiones', valor: embudo.sesiones, pct: null },
    { etiqueta: 'Llegan a Contacto', valor: embudo.sesiones_contacto, pct: embudo.pct_contacto, sobre: 'de las sesiones' },
    { etiqueta: 'Envían el formulario', valor: embudo.formularios, pct: embudo.pct_formulario, sobre: 'de los que llegan a Contacto' },
  ];
  return (
    <BloqueWeb titulo="Embudo" subtitulo="De la visita al formulario de contacto">
      <ol className="flex flex-col sm:flex-row gap-2">
        {pasos.map((p, i) => (
          <li key={p.etiqueta} className="flex-1 flex sm:flex-col items-center sm:items-stretch gap-2">
            <div className={`flex-1 w-full rounded-lg p-3 border ${i === 2 ? 'bg-emerald-50 border-emerald-200' : 'bg-gray-50'}`}>
              <div className="text-xs text-gray-500">{p.etiqueta}</div>
              <div className="text-2xl font-bold tabular-nums">{fmtInt(p.valor ?? 0)}</div>
              {p.pct !== null && <div className="text-xs text-gray-600">{fmtPct(p.pct)} {p.sobre}</div>}
            </div>
          </li>
        ))}
      </ol>
      {embudo.evento_formulario && embudo.evento_formulario !== 'formulario_enviado' && (
        <p className="mt-2 text-[11px] text-gray-400">Formularios contados con el evento {embudo.evento_formulario} mientras no llegue formulario_enviado.</p>
      )}
    </BloqueWeb>
  );
}

function DispositivosWeb({ dispositivos }) {
  const max = dispositivos.length ? Math.max(...dispositivos.map((d) => d.sesiones)) : 0;
  return (
    <BloqueWeb titulo="Dispositivos" subtitulo="Sesiones, tiempo por sesión y rebote">
      {dispositivos.length === 0 ? (
        <p className="text-sm text-gray-500">Sin datos de dispositivos.</p>
      ) : (
        <ul className="space-y-3">
          {dispositivos.map((d) => (
            <BarraHorizontal
              key={d.dispositivo}
              etiqueta={`${NOMBRES_DISPOSITIVO[d.dispositivo] || d.dispositivo} (${fmtPct(d.pct_sesiones)})`}
              valor={d.sesiones}
              maximo={max}
              detalle={`${segundosLegibles(d.segundos_por_sesion)} por sesión · rebote ${tasaPct(d.rebote)}`}
            />
          ))}
        </ul>
      )}
    </BloqueWeb>
  );
}

function HorasWeb({ horas }) {
  const valores = Array.from({ length: 24 }, (_, h) => Number(horas[h]) || 0);
  const max = Math.max(...valores);
  return (
    <BloqueWeb titulo="Horas" subtitulo="Sesiones por hora del día (hora de la propiedad GA4)">
      {max === 0 ? (
        <p className="text-sm text-gray-500">Sin datos por hora.</p>
      ) : (
        <>
          <div className="flex items-end gap-0.5 h-28" aria-label="Sesiones por hora">
            {valores.map((v, h) => (
              <div key={`hora-${h}`} className="flex-1 flex flex-col justify-end h-full" title={`${h}:00 · ${fmtInt(v)} sesiones`}>
                <div className="bg-duetto-secondary rounded-t" style={{ height: `${Math.round((v / max) * 100)}%`, minHeight: v ? '2px' : 0 }} />
              </div>
            ))}
          </div>
          <div className="flex gap-0.5 mt-1 text-[10px] text-gray-400">
            {valores.map((_, h) => (
              <span key={`etq-${h}`} className="flex-1 text-center">{h % 3 === 0 ? h : ''}</span>
            ))}
          </div>
        </>
      )}
    </BloqueWeb>
  );
}

function PaginasEntrada({ landing }) {
  return (
    <BloqueWeb titulo="Páginas de entrada" subtitulo="Dónde empieza la visita. Rebote = sesiones sin interacción.">
      {landing.length === 0 ? (
        <p className="text-sm text-gray-500">Sin páginas de entrada en el periodo.</p>
      ) : (
        <div className="overflow-x-auto -mx-4 px-4">
          <table className="min-w-full text-sm">
            <thead>
              <tr className="text-left text-xs text-gray-500 border-b">
                <th className="py-2 pr-3 font-medium">Página</th>
                <th className="py-2 px-2 font-medium text-right whitespace-nowrap">Sesiones</th>
                <th className="py-2 px-2 font-medium text-right whitespace-nowrap">Rebote</th>
                <th className="py-2 pl-2 font-medium text-right whitespace-nowrap">Conversiones</th>
              </tr>
            </thead>
            <tbody>
              {landing.slice(0, 15).map((l) => (
                <tr key={l.path} className="border-b last:border-0">
                  <td className="py-2 pr-3 font-mono text-xs text-gray-800 break-all min-w-[10rem]">{l.path}</td>
                  <td className="py-2 px-2 text-right tabular-nums">{fmtInt(l.sesiones)}</td>
                  <td className="py-2 px-2 text-right tabular-nums">{tasaPct(l.rebote)}</td>
                  <td className="py-2 pl-2 text-right tabular-nums">{fmtInt(l.conversiones)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </BloqueWeb>
  );
}
